import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { 
  FaTimes, FaUser, FaPhone, FaWeight, FaVenusMars, 
  FaClock, FaTag, FaRupeeSign, FaSyringe, 
  FaNotesMedical, FaHistory, FaCalendar, FaFilePrescription,
  FaStethoscope, FaClipboardList, FaChevronDown, FaChevronUp,
  FaPlus
} from "react-icons/fa";
import { format, parseISO, isValid } from "date-fns";
import { useColor } from "../../hooks/color/useColor";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { getPetIcon } from "../../components/common/PetDetailsBadge";
import { httpService } from "../../services/httpService";
import PetHeader from "./PetHeader";
import PetsSOAP from "../client_pets/PetsSOAP";       
import PetSOAPSummary from "../client_pets/PetSOAPSummary";
import { SOAPVisitCard } from "../client_pets/SOAPVisitCard";
const VetPetDetailsDrawer = ({
  isOpen,
  onClose,
  petData,
  clientData,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("soap");
  const [petDetails, setPetDetails] = useState(petData || {});
  const [clientDetails, setClientDetails] = useState(clientData || {});
  const [vetOrders, setVetOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [vaccinations, setVaccinations] = useState([]);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [soapNotes, setSoapNotes] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [expandedSoap, setExpandedSoap] = useState(null);
  const { getColor } = useColor();
  const [soapSummaries, setSoapSummaries] = useState({});

  // Format pet details
  const petName = petDetails?.name || "Unknown Pet";
  const petType = petDetails?.pet_type?.name || petDetails?.pet_type_name || "Dog";
  const petBreed = petDetails?.breed?.breed_name || petDetails?.breed_name || "Unknown Breed";
  const petAge = petDetails?.age || "Unknown";
  const petGender = petDetails?.gender || "Unknown";
  const petWeight = petDetails?.weight_kg || petDetails?.weight || "N/A";
  const petImage = petDetails?.image || petDetails?.pet_images?.[0] || null;
  const petCoat = petDetails?.hair_type || petDetails?.breed?.breed_coat || petDetails?.coat || "—";
  const petTags = petDetails?.tags || petDetails?.pet_tags || [];
  const petColor = petDetails?.color || petDetails?.colors || petDetails?.pet_color || [];
  const petDiet = petDetails?.meal_type || petDetails?.mealType || petDetails?.diet || petDetails?.what_to_feed || [];
  const petMealsPerDay = petDetails?.meals_per_day || petDetails?.mealsPerDay || null;
  const petSize = petDetails?.size?.size_label || petDetails?.size?.name || petDetails?.size || "";
  const petNeuteredStatus = petDetails?.neutered_status || petDetails?.neutered_spayed || "Unknown";
  const petMedicalConditions = petDetails?.medical_conditions || petDetails?.medical_condition || [];
  const petVaccinations = petDetails?.vaccinations || petDetails?.vaccinated_for || petDetails?.vaccines || [];
  const petId = petDetails?._id || petDetails?.vendor_pet_id;
  const clientId = petDetails?.vendor_client_id || clientData?._id; 

  const tabs = [
    { id: "soap", label: "SOAP", icon: FaClipboardList, iconColor: "text-blue-500" },
    { id: "overview", label: "Overview", icon: FaHistory, iconColor: "text-gray-500" },
    { id: "vaccinations", label: "Vaccinations", icon: FaSyringe, iconColor: "text-green-500" },
  ];

  // Client details
  const clientName = clientDetails?.firstname && clientDetails?.lastname 
    ? `${clientDetails.firstname} ${clientDetails.lastname}` 
    : clientDetails?.name || "Unknown Owner";
  const clientPhone = clientDetails?.phone || clientDetails?.mobile || "N/A";

  // Fetch client data
  const fetchClientData = async () => {
    if (!clientId || !petId) {
      console.warn("Missing clientId or petId");
      return;
    }

    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorclient/getVendorClientPetsOverviewById/${clientId}`
      );
      
      if (response?.code === 200 && response?.data?.data) {
        const clientDataFromApi = response.data.data;
        
        // Update client details
        setClientDetails(prev => ({
          ...prev,
          firstname: clientDataFromApi.firstname || prev?.firstname,
          lastname: clientDataFromApi.lastname || prev?.lastname,
          phone: clientDataFromApi.phone || prev?.phone,
          email: clientDataFromApi.email || prev?.email,
          address: clientDataFromApi.address || prev?.address,
        }));

        // Extract pet details from response
        let petDataFromApi = null;
        if (clientDataFromApi.pet_details) {
          petDataFromApi = clientDataFromApi.pet_details.find(p => p._id === petId);
          if (petDataFromApi) {
            setPetDetails(petDataFromApi);
          }
        }

        const petData = petDataFromApi || petDetails;

        // Process orders
        const processedOrderIds = new Set();
        const allOrdersList = [];
        const vetOrdersList = [];
        
        const processOrder = (order) => {
          if (!order || !order.items || !Array.isArray(order.items)) return;
          
          order.items.forEach(item => {
            const itemPetId = item.vendor_pet_id || item.pet_id || 
              item.appointment_info?.vendor_pet_id || 
              item.boarding_info?.vendor_pet_id;
            
            if (itemPetId === petId) {
              const uniqueKey = `${order._id}_${item._id || item.ref_id}`;
              if (processedOrderIds.has(uniqueKey)) return;
              processedOrderIds.add(uniqueKey);
              
              const booking = extractBookingFromItem(item, order);
              if (booking) {
                allOrdersList.push(booking);
                if (booking.type === "vet_clinic") {
                  vetOrdersList.push(booking);
                }
              }
            }
          });
        };

        // Process all order sources
        if (clientDataFromApi.order_details) {
          clientDataFromApi.order_details.forEach(processOrder);
        }
        
        if (clientDataFromApi.upcoming_appointment_details) {
          clientDataFromApi.upcoming_appointment_details.forEach(processOrder);
        }
        
        if (clientDataFromApi.completed_cancelled_appointments) {
          clientDataFromApi.completed_cancelled_appointments.forEach(processOrder);
        }

        allOrdersList.sort((a, b) => new Date(b.date) - new Date(a.date));
        vetOrdersList.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Set recent visits (last 5 vet visits)
        const recent = vetOrdersList.slice(0, 5);
        setRecentVisits(recent);

        setAllOrders(allOrdersList);
        setVetOrders(vetOrdersList);
        
        const soapResults = await Promise.allSettled(
        vetOrdersList.map((v) => httpService.get(`/vendorpet/getvendorsoap/${v.ref_id}`))
          );
          const summaryMap = {};
          soapResults.forEach((res, idx) => {
            const refId = vetOrdersList[idx].ref_id;
            if (res.status === "fulfilled") {
              const payload = res.value?.data;
              // adjust once you confirm the real shape — see note below
              summaryMap[refId] = Array.isArray(payload?.notes) ? payload.notes : payload ? [payload] : [];
            } else {
              summaryMap[refId] = [];
            }
          });
          setSoapSummaries(summaryMap);

        // Set vaccinations
        let vaccineData = [];
        if (petData.vaccination_records && petData.vaccination_records.length > 0) {
          vaccineData = petData.vaccination_records;
        } else if (petData.vaccinations && Array.isArray(petData.vaccinations)) {
          vaccineData = petData.vaccinations.map(v => ({ vaccine: v }));
        }
        setVaccinations(vaccineData);

        // Set medical conditions
        if (petData.medical_condition && petData.medical_condition.length > 0) {
          setMedicalConditions(petData.medical_condition);
        }

        // Extract SOAP notes from vet orders
        const soapData = vetOrdersList
          .filter(b => b.notes || b.diagnosis)
          .map(b => ({
            date: b.date,
            subjective: b.complaint || b.notes || "N/A",
            objective: b.diagnosis || "N/A",
            assessment: b.assessment || "N/A",
            plan: b.treatment || b.plan || "N/A",
            vet_name: b.employee_name,
            additional_notes: b.additional_notes || ""
          }));
        setSoapNotes(soapData);

        // Extract prescriptions
        const prescriptionData = vetOrdersList
          .filter(b => b.prescription || b.treatment)
          .map(b => ({
            medication: b.prescription?.medication || b.treatment || b.service_name,
            dosage: b.prescription?.dosage || "As prescribed",
            duration: b.prescription?.duration || "N/A",
            prescribed_date: b.date,
            vet_name: b.employee_name,
            status: b.status
          }));
        setPrescriptions(prescriptionData);

        setLoading(false);
      } else {
        console.error("❌ API returned unexpected response:", response);
        setLoading(false);
      }
    } catch (error) {
      console.error("❌ Error fetching client data:", error);
      setLoading(false);
    }
  };

  // Helper to extract booking from item
  const extractBookingFromItem = (item, order) => {
    try {
      const isVet = item.type === "vet_clinic";
      
      let serviceInfo = {};
      let date = null;
      let employeeName = "N/A";
      let serviceName = "Service";
      let type = item.type;
      let status = item.status || order.status || "pending";
      let price = item.final_total || item.total_price || item.price || 0;
      let paidAmount = item.total_paid_amount || item.paid_amount || item.paid || 0;
      let orderNumber = order.order_number || order._id;
      let orderId = order._id;
      let startTime = null;
      let duration = null;
      let diagnosis = "N/A";
      let treatment = "N/A";
      let complaint = "N/A";
      let notes = "";
      let prescription = null;
      let assessment = "N/A";
      let plan = "N/A";
      let additional_notes = "";

      if (isVet && item.appointment_info) {
        serviceInfo = item.appointment_info;
        date = serviceInfo.appointment_date || order.createdAt;
        employeeName = serviceInfo.employee_details?.name || serviceInfo.employee_name || "N/A";
        // FIX: Get service name from vetclinic_services
        serviceName = serviceInfo.vetclinic_services?.name || item.service_name || "Vet Service";
        status = serviceInfo.status || status;
        type = "vet_clinic";
        startTime = serviceInfo.time_slot || item.start_time || null;
        duration = serviceInfo.duration || item.duration || null;
        notes = serviceInfo.notes || "";
        // FIX: Use diagnosis from service name if not available
        diagnosis = item.diagnosis || serviceInfo.diagnosis || serviceName || "N/A";
        treatment = item.treatment || serviceInfo.treatment || "N/A";
        complaint = item.complaint || serviceInfo.complaint || "N/A";
        prescription = item.prescription || serviceInfo.prescription || null;
        assessment = item.assessment || serviceInfo.assessment || "N/A";
        plan = item.plan || serviceInfo.plan || "N/A";
        additional_notes = item.additional_notes || serviceInfo.additional_notes || "";
    }else {
        return null; // Skip non-vet items
      }

      if (!date) return null;

      return {
        date: date,
        service_name: serviceName,
        price: Number(price).toFixed(1),
        paid_amount: Number(paidAmount) || 0,
        employee_name: employeeName,
        status: status,
        order_id: orderId,
        order_number: orderNumber,
        type: type,
        start_time: startTime,
        duration: duration,
        diagnosis: diagnosis,
        treatment: treatment,
        complaint: complaint,
        notes: notes,
        prescription: prescription,
        assessment: assessment,
        plan: plan,
        additional_notes: additional_notes,
        isVet: true,
        ref_id: item.ref_id || item._id || serviceInfo?.ref_id || null,
      };
    } catch (error) {
      console.error("Error extracting booking:", error);
      return null;
    }
  };

  // Process data on mount
  useEffect(() => {
    if (isOpen && clientId && petId) {
      fetchClientData();
    } else {
      setAllOrders([]);
      setVetOrders([]);
    }
  }, [isOpen, clientId, petId]);

  // Calculate stats
  const totalVisits = allOrders.length;
  const totalSpend = allOrders.reduce((sum, item) => sum + Number(item.price || 0), 0);
  // const outstanding = allOrders
  //   .filter(b => b.status?.toLowerCase() === "pending" || b.status?.toLowerCase() === "unpaid")
  //   .reduce((sum, item) => sum + Number(item.price || 0), 0);
  const totalPaid = allOrders.reduce((sum, item) => sum + Number(item.paid_amount || 0), 0);
  const outstanding = Math.max(totalSpend - totalPaid, 0);
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return "N/A";
      return format(date, "dd MMM yyyy");
    } catch {
      return "N/A";
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      confirmed: "bg-yellow-100 text-yellow-700",
      started: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
      pending: "bg-orange-100 text-orange-700",
      cancelled: "bg-red-100 text-red-700",
      "no show": "bg-red-100 text-red-700",
    };
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
  };

   const splitLoose = (str) =>
  (str || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean);

const isMeaningful = (val) =>
  val !== undefined && val !== null && val !== "" &&
  !(Array.isArray(val) && val.length === 0) && val !== "No" && val !== "Normal";

const buildSoapPreview = (note) => {
  if (!note) return null;
  const diagnosis = splitLoose(note.assessment?.diagnosis);
  if (diagnosis.length) return diagnosis.join(", ");
  if (isMeaningful(note.subjective?.diet) || note.subjective?.vomiting?.present === "Yes") {
    return "Subjective findings recorded";
  }
  return note.summary?.serviceNote || null;
};

 const recentVetOrders = vetOrders.slice(0, 5);

 const renderSOAP = () => {
  return (
    <div className="space-y-4">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {recentVetOrders.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No SOAP notes available</p>
        ) : (
          recentVetOrders.map((visit, idx) => {
            const notes = soapSummaries[visit.ref_id] || [];
            return (
              <div key={idx} className="border border-gray-100 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{formatDate(visit.date)}</p>
                    <p className="text-sm font-medium text-gray-800">{visit.service_name}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(visit.status)}`}>
                    {visit.status || "N/A"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 mb-2">Vet: {visit.employee_name}</p>

                {notes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No SOAP note recorded for this visit.</p>
                ) : (
                  <>
                    {notes.length > 1 && (
                      <p className="text-xs text-gray-400 mb-1">{notes.length} SOAP notes for this visit</p>
                    )}
                    {notes.map((note, noteIdx) => (
                      <SOAPVisitCard key={note._id || noteIdx} note={note} defaultOpen={false} />
                    ))}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

  // Render Vaccinations Tab
  const renderVaccinations = () => {
    return (
      <div className="space-y-3">
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {vaccinations.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No vaccinations recorded</p>
          ) : (
            vaccinations.map((v, idx) => {
              const isExpired = v.next_due && new Date(v.next_due) < new Date();
              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={isExpired ? "text-red-500 text-lg" : "text-green-500 text-lg"}>
                        {isExpired ? "🔴" : "🟢"}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{v.vaccine || v.name}</p>
                        {v.date_given && (
                          <p className="text-xs text-gray-500">Given: {formatDate(v.date_given)}</p>
                        )}
                      </div>
                    </div>
                    {v.next_due && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isExpired ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                      }`}>
                        {isExpired ? "Expired" : `Valid until ${formatDate(v.next_due)}`}
                      </span>
                    )}
                  </div>
                  {v.batch_number && (
                    <p className="text-xs text-gray-400 mt-1">Batch: {v.batch_number}</p>
                  )}
                  {v.administered_by && (
                    <p className="text-xs text-gray-400">Administered by: {v.administered_by}</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };
    // Render Overview Tab - Stats + Recent Visits
   const renderOverview = () => {
    return (
      <div className="space-y-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-blue-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-blue-600">{totalVisits}</p>
            <p className="text-[10px] text-gray-600">Total Visits</p>
          </div>
          <div className="bg-green-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-green-600">₹{totalSpend.toFixed(0)}</p>
            <p className="text-[10px] text-gray-600">Total Spend</p>
          </div>
          <div className="bg-red-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-red-600">₹{outstanding.toFixed(0)}</p>
            <p className="text-[10px] text-gray-600">Outstanding</p>
          </div>
        </div>
      </div>
    );
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div className="h-full w-full lg:w-[60%] xl:w-[55%] bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        
        {/* Pet Header with Close Button */}
        <PetHeader
          serviceName="Vet"
          petName={petName}
          petType={petType}
          petBreed={petBreed}
          petGender={petGender}
          petAge={petAge}
          petWeight={petWeight}
          petNeuteredStatus={petNeuteredStatus}
          petImage={petImage}
          petCoat={petCoat}
          petTags={petTags}
          petColor={petColor}
          petDiet={petDiet}
          petMealsPerDay={petMealsPerDay}
          petSize={petSize}
          petDetails={petDetails}
          medicalConditions={petMedicalConditions}
          vaccinations={petVaccinations}
          clientName={clientName}
          clientPhone={clientPhone}
          onClose={onClose}
          formatDate={formatDate}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 text-center text-sm font-semibold transition-colors relative flex items-center justify-center gap-1.5 ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-blue-600" : tab.iconColor}`} />
                  {tab.label}
                  {isActive && (
                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          {loading ? (
            <LoaderSpiner text="Loading..." />
          ) : (
            <>
              {activeTab === "soap" && renderSOAP()}
              {activeTab === "overview" && renderOverview()}
              {activeTab === "vaccinations" && renderVaccinations()}
              
            </>
          )}
        </div>
      </div>
    </div>
  );
};

VetPetDetailsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  petData: PropTypes.object,
  clientData: PropTypes.object,
  clientAppointments: PropTypes.array,
  clientBoarding: PropTypes.array,
  onRefresh: PropTypes.func,
};

export default VetPetDetailsDrawer;